// @ts-nocheck — aspirational data shapes / API mismatch; tracked under Task #75 (type-safety debt)
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import PLATFORM_URLS from "@/config/platform-urls";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  Menu, 
  User, 
  LogOut, 
  UserPlus, 
  ChevronDown,
  Package,
  Globe,
  Truck,
  Info,
  Home,
  Mail,
  Users,
  Network,
  MessageSquare,
  Shield,
  FileSearch,
  LayoutDashboard,
  ExternalLink,
  Briefcase,
  Coins
} from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/common/session/LanguageSwitcher";
import { WebSocketHeaderIndicator } from "@/components/common/status/WebSocketStatusIndicator";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/use-auth";
import { useCMSMenu } from "@/hooks/use-cms";
import { cn } from "@/lib/utils";

const NavDropdown = ({ 
  trigger, 
  items, 
  isActive 
}: { 
  trigger: string; 
  items: { href: string; label: string; icon?: React.ReactNode; description?: string; external?: boolean }[];
  isActive: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200",
            "hover:bg-accent/50",
            isActive && "bg-primary/10 text-primary",
            !isActive && "text-foreground/80 hover:text-foreground"
          )}
        >
          {trigger}
          <ChevronDown className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            open && "rotate-180"
          )} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 p-2">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.href}
            onClick={() => {
              if (item.external || item.href.startsWith("http://") || item.href.startsWith("https://")) {
                window.open(item.href, "_blank", "noopener,noreferrer");
              } else {
                navigate(item.href);
              }
              setOpen(false);
            }}
            className="flex items-start gap-3 p-3 cursor-pointer rounded-md hover:bg-accent"
          >
            {item.icon && (
              <div className="mt-0.5 text-muted-foreground">
                {item.icon}
              </div>
            )}
            <div className="flex flex-col gap-1 flex-1">
              <span className="font-medium flex items-center gap-1.5">
                {item.label}
                {(item.external || item.href.startsWith("http")) && (
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
              {item.description && (
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {item.description}
                </span>
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const AuthButton = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => window.location.href = "/login"}
        className="text-foreground/80 hover:text-foreground"
      >
        {t("auth.actions.login", "Log in")}
      </Button>
      <Button 
        size="sm"
        onClick={() => window.location.href = "/register"}
        className="bg-primary hover:bg-primary/90"
      >
        {t("auth.actions.register", "Get Started")}
      </Button>
    </div>
  );
};

export const ProfileMenu = ({ user, onLogout }: { user: any; onLogout: () => Promise<void> }) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  
  const isAdmin = ["admin", "super_admin", "manager"].includes(user?.role || "");
  const displayName = user?.displayName || user?.fullName || user?.username || "User";
  const initials = displayName.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-9 w-9 rounded-full ring-2 ring-transparent hover:ring-primary/20 transition-all">
          <Avatar className="h-9 w-9">
            {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={displayName} />}
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-sm font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal p-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={displayName} />}
              <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-sm font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col space-y-0.5 min-w-0">
              <p className="text-sm font-semibold leading-none truncate">{displayName}</p>
              <p className="text-xs leading-none text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/dashboard")} className="py-2.5">
          <LayoutDashboard className="mr-2.5 h-4 w-4 text-muted-foreground" />
          <span>{t("nav.dashboard", "Dashboard")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/profile")} className="py-2.5">
          <User className="mr-2.5 h-4 w-4 text-muted-foreground" />
          <span>{t("profile.menu.profile", "My Profile")}</span>
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => navigate("/admin/master")}
              className="py-2.5 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-950/30 dark:to-indigo-950/30"
            >
              <Shield className="mr-2.5 h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="font-medium text-blue-700 dark:text-blue-300">{t("nav.controlCenter", "Control Center")}</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={() => window.open(PLATFORM_URLS.opt, "_blank", "noopener,noreferrer")}
          className="py-2.5"
        >
          <Package className="mr-2.5 h-4 w-4 text-muted-foreground" />
          <span>OPT Platform</span>
          <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => window.open(PLATFORM_URLS.mololink, "_blank", "noopener,noreferrer")}
          className="py-2.5"
        >
          <Network className="mr-2.5 h-4 w-4 text-muted-foreground" />
          <span>MoloLink</span>
          <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout} className="py-2.5 text-red-600 dark:text-red-400 focus:text-red-600">
          <LogOut className="mr-2.5 h-4 w-4" />
          <span>{t("profile.menu.logout", "Log out")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Navigation = () => {
  const [location, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isHome = location === "/" || location === "";
  const { t } = useTranslation();
  const { user, logoutMutation } = useAuth();
  const { data: cmsMenu } = useCMSMenu();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {}
  };

  const navigationGroups = {
    services: {
      trigger: t("nav.services", "Services"),
      items: [
        { 
          href: "/services", 
          label: t("nav.allServices", "All Services"),
          icon: <Package className="h-4 w-4" />,
          description: t("nav.allServicesDesc", "View our complete service catalog")
        },
        { 
          href: "/tracking", 
          label: t("nav.trackShipment", "Track Shipment"),
          icon: <Truck className="h-4 w-4" />,
          description: t("nav.trackShipmentDesc", "Real-time shipment tracking")
        },
        { 
          href: "/contact", 
          label: t("nav.quote", "Get a Quote"),
          icon: <MessageSquare className="h-4 w-4" />,
          description: t("nav.quoteDesc", "Request a custom quote")
        }
      ]
    },
    ecosystem: {
      trigger: t("nav.ecosystem", "Ecosystem"),
      items: [
        { 
          href: PLATFORM_URLS.mololink, 
          label: t("nav.mololink", "MoloLink"),
          icon: <Network className="h-4 w-4" />,
          description: t("nav.mololinkDesc", "Professional logistics network"),
          external: true
        },
        { 
          href: PLATFORM_URLS.opt, 
          label: "OPT Platform",
          icon: <Package className="h-4 w-4" />,
          description: "Operations & Transport Management",
          external: true
        },
        { 
          href: "/partners", 
          label: t("nav.partners", "Partners"),
          icon: <Users className="h-4 w-4" />,
          description: t("nav.partnersDesc", "Our trusted partners")
        },
        {
          href: PLATFORM_URLS.molo,
          label: t("nav.token", "Token"),
          icon: <Coins className="h-4 w-4" />,
          description: t("nav.tokenDesc", "MoloChain utility token"),
          external: true
        },
      ]
    },
    company: {
      trigger: t("nav.company", "Company"),
      items: [
        { 
          href: "/about", 
          label: t("nav.aboutUs", "About Us"),
          icon: <Info className="h-4 w-4" />,
          description: t("nav.aboutDesc", "Learn about MoloChain")
        },
        { 
          href: "/careers", 
          label: t("nav.careers", "Careers"),
          icon: <Briefcase className="h-4 w-4" />,
          description: t("nav.careersDesc", "Join our team")
        },
        { 
          href: "/contact", 
          label: t("nav.contact", "Contact"),
          icon: <Mail className="h-4 w-4" />,
          description: t("nav.contactShortDesc", "Get in touch with us")
        },
        { 
          href: "/terms", 
          label: t("nav.termsOfService", "Terms of Service"),
          icon: <FileSearch className="h-4 w-4" />,
          description: t("nav.termsDesc", "Legal terms and policies")
        },
        { 
          href: "/privacy", 
          label: t("nav.privacyPolicy", "Privacy Policy"),
          icon: <Shield className="h-4 w-4" />,
          description: t("nav.privacyDesc", "Data protection and privacy")
        }
      ]
    }
  };

  const isDropdownActive = (items: { href: string }[]) => {
    return items.some(item => location === item.href);
  };

  const renderMainNav = () => {
    return (
      <>
        <button
          onClick={() => navigate("/")}
          className={cn(
            "px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200",
            "hover:bg-accent/50",
            location === "/" && "bg-primary/10 text-primary",
            location !== "/" && "text-foreground/80 hover:text-foreground"
          )}
        >
          {t("nav.home", "Home")}
        </button>

        <NavDropdown
          key="services"
          trigger={navigationGroups.services.trigger}
          items={navigationGroups.services.items}
          isActive={isDropdownActive(navigationGroups.services.items)}
        />

        <NavDropdown
          key="ecosystem"
          trigger={navigationGroups.ecosystem.trigger}
          items={navigationGroups.ecosystem.items}
          isActive={isDropdownActive(navigationGroups.ecosystem.items)}
        />

        <NavDropdown
          key="company"
          trigger={navigationGroups.company.trigger}
          items={navigationGroups.company.items}
          isActive={isDropdownActive(navigationGroups.company.items)}
        />
      </>
    );
  };

  return (
    <nav className={cn(
      "fixed top-0 w-full z-50 transition-all duration-300",
      scrolled 
        ? "bg-background/95 backdrop-blur-lg shadow-sm border-b" 
        : isHome
          ? "bg-transparent"
          : "bg-background/80 backdrop-blur-sm border-b border-border/30"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link href="/">
              <div className="flex items-center gap-2 group">
                <picture>
                  <source srcSet="/molochain-logo.webp" type="image/webp" />
                  <img 
                    src="/molochain-logo.png"
                    alt="MOLOCHAIN" 
                    className="h-9 w-auto transition-transform duration-200 group-hover:scale-105"
                  />
                </picture>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {renderMainNav()}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <WebSocketHeaderIndicator />
            </div>
            
            <div className="hidden md:block">
              <ThemeToggle />
            </div>

            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            <div className="hidden md:block">
              {user ? <ProfileMenu user={user} onLogout={handleLogout} /> : <AuthButton />}
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="lg:hidden"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[360px] flex flex-col p-0">
                <div className="flex items-center gap-3 p-5 pb-4 flex-shrink-0 border-b">
                  <picture>
                    <source srcSet="/molochain-logo.webp" type="image/webp" />
                    <img 
                      src="/molochain-logo.png"
                      alt="MOLOCHAIN" 
                      className="h-8 w-auto"
                    />
                  </picture>
                </div>

                {user && (
                  <div className="px-5 py-4 border-b bg-muted/30">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user?.username} />}
                        <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-sm font-semibold">
                          {(user?.username || "U").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{user?.displayName || user?.username}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-xs"
                        onClick={() => { navigate("/dashboard"); setOpen(false); }}
                      >
                        <LayoutDashboard className="h-3.5 w-3.5 mr-1.5" />
                        Dashboard
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 text-xs"
                        onClick={() => { navigate("/profile"); setOpen(false); }}
                      >
                        <User className="h-3.5 w-3.5 mr-1.5" />
                        Profile
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto">
                  <div className="flex flex-col gap-0.5 p-4">
                    <button
                      className={cn(
                        "w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all",
                        location === "/" 
                          ? "bg-primary/10 text-primary" 
                          : "hover:bg-accent"
                      )}
                      onClick={() => {
                        navigate("/");
                        setOpen(false);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Home className="h-4 w-4" />
                        {t("nav.home", "Home")}
                      </div>
                    </button>

                    {Object.entries(navigationGroups).map(([key, group]) => (
                      <div key={key} className="mt-3">
                        <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {group.trigger}
                        </div>
                        <div className="space-y-0.5">
                          {group.items.map((item) => (
                            <button
                              key={item.href}
                              className={cn(
                                "w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all",
                                location === item.href 
                                  ? "bg-primary/10 text-primary" 
                                  : "hover:bg-accent"
                              )}
                              onClick={() => {
                                if (item.external || item.href.startsWith("http")) {
                                  window.open(item.href, "_blank", "noopener,noreferrer");
                                } else {
                                  navigate(item.href);
                                }
                                setOpen(false);
                              }}
                            >
                              <div className="flex items-center gap-3">
                                {item.icon}
                                <span className="flex-1">{item.label}</span>
                                {(item.external || item.href.startsWith("http")) && (
                                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="mt-3">
                      <button
                        className="w-full text-left px-4 py-2.5 rounded-lg text-sm transition-all hover:bg-accent"
                        onClick={() => {
                          window.open(PLATFORM_URLS.molo, "_blank", "noopener,noreferrer");
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <Coins className="h-4 w-4" />
                          <span className="flex-1">{t("nav.token", "Token")}</span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t p-4 flex-shrink-0 space-y-3">
                  <div className="flex items-center justify-between">
                    <LanguageSwitcher />
                    <ThemeToggle />
                  </div>
                  {!user && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => { window.location.href = "/login"; }}
                      >
                        Log in
                      </Button>
                      <Button 
                        size="sm" 
                        className="flex-1"
                        onClick={() => { window.location.href = "/register"; }}
                      >
                        Get Started
                      </Button>
                    </div>
                  )}
                  {user && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
                      onClick={handleLogout}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t("profile.menu.logout", "Log out")}
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
